import { useState, useEffect, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import { Loader2, Users, Plus, Trash2, Edit2, Save, RefreshCw, Target, ShuffleIcon } from "lucide-react";

const CURRENT_FY_START = (() => {
  const d = new Date();
  return d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
})();

const fyLabel = (y) => `FY ${y}-${String(y + 1).slice(-2)}`;
const fmtInr = (v) => `₹${new Intl.NumberFormat("en-IN").format(Math.round(v || 0))}`;

export default function SalesTeamSettings() {
  const [members, setMembers] = useState([]);
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingMember, setSavingMember] = useState(false);
  const [fyYear, setFyYear] = useState(CURRENT_FY_START);

  // Add/edit member dialog
  const [showMemberDialog, setShowMemberDialog] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [memberForm, setMemberForm] = useState({ name: "", active: true });

  // Target editing (inline)
  const [targetDrafts, setTargetDrafts] = useState({}); // memberId -> amount string
  const [savingTargetMid, setSavingTargetMid] = useState(null);

  // Bulk reassign
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignMemberId, setReassignMemberId] = useState("");
  const [reassignFilters, setReassignFilters] = useState({ date_start: "", date_end: "", only_unassigned: true });
  const [reassigning, setReassigning] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [m, t] = await Promise.all([
        api.get("/collections/sales-team"),
        api.get("/collections/sales-team-targets"),
      ]);
      setMembers(m.data || []);
      setTargets(t.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load sales team");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openNewMember = () => {
    setEditingMember(null);
    setMemberForm({ name: "", active: true });
    setShowMemberDialog(true);
  };
  const openEditMember = (m) => {
    setEditingMember(m);
    setMemberForm({ name: m.name, active: m.active });
    setShowMemberDialog(true);
  };
  const saveMember = async () => {
    if (!memberForm.name.trim()) { toast.error("Name is required"); return; }
    setSavingMember(true);
    try {
      if (editingMember) {
        await api.put(`/collections/sales-team/${editingMember.id}`, memberForm);
        toast.success("Updated");
      } else {
        await api.post("/collections/sales-team", memberForm);
        toast.success("Added");
      }
      setShowMemberDialog(false);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setSavingMember(false);
    }
  };
  const deleteMember = async (m) => {
    if (!window.confirm(`Delete "${m.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/collections/sales-team/${m.id}`);
      toast.success("Deleted");
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  const targetForMember = (memberId) =>
    targets.find(t => t.sales_member_id === memberId && t.fy_start_year === fyYear);

  const draftValue = (memberId) => {
    if (targetDrafts[memberId] !== undefined) return targetDrafts[memberId];
    const t = targetForMember(memberId);
    return t ? String(t.annual_target) : "";
  };

  const saveTarget = async (memberId) => {
    const raw = draftValue(memberId);
    const amt = Number(raw);
    if (isNaN(amt) || amt < 0) { toast.error("Enter a valid non-negative number"); return; }
    setSavingTargetMid(memberId);
    try {
      await api.post("/collections/sales-team-targets", {
        sales_member_id: memberId,
        fy_start_year: fyYear,
        annual_target: amt,
      });
      toast.success("Target saved");
      setTargetDrafts(d => { const n = { ...d }; delete n[memberId]; return n; });
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSavingTargetMid(null);
    }
  };

  const runBulkReassign = async () => {
    if (!reassignMemberId) { toast.error("Pick a sales member"); return; }
    setReassigning(true);
    try {
      const r = await api.post("/collections/sales-team/bulk-reassign", {
        sales_member_id: reassignMemberId,
        only_unassigned: reassignFilters.only_unassigned,
        date_start: reassignFilters.date_start || undefined,
        date_end: reassignFilters.date_end || undefined,
      });
      toast.success(`${r.data.modified} receipt(s) reassigned to ${r.data.assigned_to}`);
      setReassignOpen(false);
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reassignment failed");
    } finally {
      setReassigning(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="sales-team-settings">
      {/* Members section */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2"><Users className="w-4 h-4 text-[#0066CC]" /> Sales Team Members</h2>
              <p className="text-xs text-slate-500">People who make collections. Referenced from every new receipt.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setReassignOpen(true)} data-testid="open-bulk-reassign-btn">
                <ShuffleIcon className="w-4 h-4 mr-1" /> Bulk Reassign Receipts
              </Button>
              <Button size="sm" onClick={openNewMember} className="bg-[#0066CC] hover:bg-[#0052A3]" data-testid="add-sales-member-btn">
                <Plus className="w-4 h-4 mr-1" /> Add Member
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : members.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No sales members yet. Add your first one to start tracking collections per person.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="sales-members-table">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-500">
                    <th className="text-left py-2 pr-3 font-semibold">Name</th>
                    <th className="text-left py-2 pr-3 font-semibold">Status</th>
                    <th className="text-right py-2 pr-3 font-semibold">Receipts</th>
                    <th className="text-right py-2 pr-3 font-semibold">Total Collected</th>
                    <th className="text-right py-2 pr-3 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map(m => (
                    <tr key={m.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2.5 pr-3 font-medium text-slate-800">{m.name}</td>
                      <td className="py-2.5 pr-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${m.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                          {m.active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="py-2.5 pr-3 text-right text-slate-500">{m.receipt_count || 0}</td>
                      <td className="py-2.5 pr-3 text-right text-[#0066CC] font-semibold">{fmtInr(m.total_collected || 0)}</td>
                      <td className="py-2.5 pr-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="ghost" onClick={() => openEditMember(m)} data-testid={`edit-member-${m.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteMember(m)} className="text-red-500 hover:text-red-600" data-testid={`delete-member-${m.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* FY Targets section */}
      <Card>
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-base font-bold text-slate-900 flex items-center gap-2"><Target className="w-4 h-4 text-[#0066CC]" /> Sales Team FY Targets</h2>
              <p className="text-xs text-slate-500">Annual collection target per member. Automatically split into monthly / weekly / daily.</p>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs">FY</Label>
              <select value={fyYear} onChange={(e) => setFyYear(Number(e.target.value))} className="h-8 border border-slate-200 rounded px-2 text-xs bg-white" data-testid="target-fy-select">
                {[CURRENT_FY_START - 1, CURRENT_FY_START, CURRENT_FY_START + 1, CURRENT_FY_START + 2].map(y => (
                  <option key={y} value={y}>{fyLabel(y)}</option>
                ))}
              </select>
              <Button size="sm" variant="ghost" onClick={loadAll} disabled={loading}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {members.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">Add sales members first to set their targets.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="sales-targets-table">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-500">
                    <th className="text-left py-2 pr-3 font-semibold">Sales Member</th>
                    <th className="text-right py-2 pr-3 font-semibold">Annual Target (INR)</th>
                    <th className="text-right py-2 pr-3 font-semibold">Monthly ÷12</th>
                    <th className="text-right py-2 pr-3 font-semibold">Weekly ÷52</th>
                    <th className="text-right py-2 pr-3 font-semibold">Daily ÷365</th>
                    <th className="text-right py-2 pr-3 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {members.filter(m => m.active).map(m => {
                    const t = targetForMember(m.id);
                    const annual = Number(draftValue(m.id) || 0);
                    return (
                      <tr key={m.id} className="border-b border-slate-100">
                        <td className="py-2.5 pr-3 font-medium text-slate-800">{m.name}</td>
                        <td className="py-2.5 pr-3 text-right">
                          <Input
                            type="number" min={0}
                            className="h-8 text-right text-xs"
                            value={draftValue(m.id)}
                            onChange={(e) => setTargetDrafts(d => ({ ...d, [m.id]: e.target.value }))}
                            placeholder="0"
                            data-testid={`target-input-${m.id}`}
                          />
                          <p className="text-[10px] text-slate-400 mt-0.5">≈ ₹{(annual / 100000).toFixed(2)} L</p>
                        </td>
                        <td className="py-2.5 pr-3 text-right text-slate-600">{fmtInr(annual / 12)}</td>
                        <td className="py-2.5 pr-3 text-right text-slate-600">{fmtInr(annual / 52)}</td>
                        <td className="py-2.5 pr-3 text-right text-slate-600">{fmtInr(annual / 365)}</td>
                        <td className="py-2.5 pr-3 text-right">
                          <Button size="sm" onClick={() => saveTarget(m.id)} disabled={savingTargetMid === m.id} className="bg-[#0066CC] hover:bg-[#0052A3] h-7 text-[11px]" data-testid={`save-target-${m.id}`}>
                            {savingTargetMid === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
                            {t ? "Update" : "Save"}
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Member Dialog */}
      <Dialog open={showMemberDialog} onOpenChange={setShowMemberDialog}>
        <DialogContent className="max-w-[380px]">
          <DialogHeader><DialogTitle>{editingMember ? "Edit Sales Member" : "Add Sales Member"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={memberForm.name} onChange={(e) => setMemberForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Raghu Kumar" data-testid="member-name-input" />
            </div>
            <div className="flex items-center justify-between border rounded-md px-3 py-2">
              <div>
                <p className="text-xs font-medium text-slate-700">Active</p>
                <p className="text-[10px] text-slate-400">Inactive members are hidden from the receipt dropdown.</p>
              </div>
              <Switch checked={memberForm.active} onCheckedChange={(v) => setMemberForm(f => ({ ...f, active: v }))} data-testid="member-active-toggle" />
            </div>
            <Button onClick={saveMember} disabled={savingMember} className="w-full bg-[#0066CC] hover:bg-[#0052A3]" data-testid="save-member-btn">
              {savingMember ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editingMember ? "Update" : "Add"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Reassign Dialog */}
      <Dialog open={reassignOpen} onOpenChange={setReassignOpen}>
        <DialogContent className="max-w-[440px]">
          <DialogHeader><DialogTitle>Bulk Reassign Receipts</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500 mt-1">Assign a sales member to receipts that don't have one — great for cleaning up historical data or imports.</p>
          <div className="space-y-3 mt-3">
            <div>
              <Label className="text-xs">Reassign to *</Label>
              <select value={reassignMemberId} onChange={(e) => setReassignMemberId(e.target.value)} className="h-9 w-full text-sm border border-slate-200 rounded-md px-2 bg-white" data-testid="reassign-member-select">
                <option value="">— Select sales member —</option>
                {members.filter(m => m.active).map(m => (<option key={m.id} value={m.id}>{m.name}</option>))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Date from</Label>
                <Input type="date" value={reassignFilters.date_start} onChange={(e) => setReassignFilters(f => ({ ...f, date_start: e.target.value }))} />
              </div>
              <div>
                <Label className="text-xs">Date to</Label>
                <Input type="date" value={reassignFilters.date_end} onChange={(e) => setReassignFilters(f => ({ ...f, date_end: e.target.value }))} />
              </div>
            </div>
            <div className="flex items-center justify-between border rounded-md px-3 py-2">
              <div>
                <p className="text-xs font-medium text-slate-700">Only unassigned receipts</p>
                <p className="text-[10px] text-slate-400">If off, this will overwrite existing assignments in the date range.</p>
              </div>
              <Switch checked={reassignFilters.only_unassigned} onCheckedChange={(v) => setReassignFilters(f => ({ ...f, only_unassigned: v }))} />
            </div>
            <Button onClick={runBulkReassign} disabled={reassigning || !reassignMemberId} className="w-full bg-[#0066CC] hover:bg-[#0052A3]" data-testid="confirm-reassign-btn">
              {reassigning ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShuffleIcon className="w-4 h-4 mr-2" />}
              Run Reassignment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
